import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Wand2, CheckCircle2, IdCard } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { LockedCard } from "./ShopSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("auth_token")}` });
const STYLE_LABELS = { modern: "Modern", klassisch: "Klassisch", verspielt: "Verspielt", minimalistisch: "Minimalistisch" };

const LogoGenerator = ({ pageId, onUpgrade, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [locked, setLocked] = useState(false);
  const [style, setStyle] = useState("modern");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [urls, setUrls] = useState([]);
  const load = useCallback(() => axios.get(`${API}/pages/${pageId}/logo-generator`, { headers: authHeaders() }).then((r) => setInfo(r.data)).catch((e) => e.response?.status === 402 && setLocked(true)), [pageId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true); setUrls([]);
    try {
      const { data } = await axios.post(`${API}/pages/${pageId}/logo-generator`, { style, description: desc }, { headers: authHeaders() });
      load();
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: job } = await axios.get(`${API}/pages/${pageId}/logo-generator/${data.job_id}`, { headers: authHeaders() });
        if (job.status === "done") { setUrls(job.urls); break; }
        if (job.status === "failed") { toast.error("Logo-Erstellung fehlgeschlagen – es wurde nichts angerechnet. Bitte erneut versuchen."); break; }
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Logo-Erstellung fehlgeschlagen"); } finally { setBusy(false); load(); }
  };
  const apply = async (url) => {
    try { await axios.put(`${API}/pages/${pageId}/logo`, { url }, { headers: authHeaders() }); toast.success("Logo übernommen – es erscheint jetzt auf deiner Webseite."); onChanged?.(); }
    catch { toast.error("Logo konnte nicht übernommen werden"); }
  };

  if (locked) return <LockedCard text="Der KI-Logo-Generator ist im Pro- und Premium-Paket enthalten." onUpgrade={onUpgrade} testId="logo-gen-locked" />;
  if (!info) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const left = info.quota - info.used;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="logo-generator">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-semibold text-slate-900"><Wand2 className="h-5 w-5 text-indigo-600" /> KI-Logo-Generator</h3>
        <span className="text-xs text-slate-500" data-testid="logo-gen-quota">Noch {left} von {info.quota} diesen Monat</span>
      </div>
      <p className="mb-4 text-sm text-slate-600">Wähle einen Stil und/oder beschreibe dein Wunschlogo. Du erhältst 4 Entwürfe und übernimmst deinen Favoriten mit einem Klick.</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {info.styles.map((s) => (
          <button key={s} type="button" onClick={() => setStyle(style === s ? null : s)} className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${style === s ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 text-slate-700 hover:border-indigo-300"}`} data-testid={`logo-style-${s}`}>{STYLE_LABELS[s] || s}</button>
        ))}
      </div>
      <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value.slice(0, 400))} placeholder="Optional: z. B. „Ein Lenkrad mit Haken, Farben Blau und Grün“" data-testid="logo-description-input" />
      <Button onClick={generate} disabled={busy || left <= 0} className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="logo-generate-btn">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Entwürfe werden erstellt (ca. 1 Min.)…</> : "4 Entwürfe erstellen"}
      </Button>
      {urls.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {urls.map((u, i) => (
            <div key={u} className="rounded-xl border border-slate-200 p-2 text-center" data-testid={`logo-result-${i}`}>
              <img src={u} alt={`Logo-Entwurf ${i + 1}`} className="mx-auto aspect-square w-full rounded-lg object-contain" />
              <Button size="sm" variant="outline" onClick={() => apply(u)} className="mt-2 w-full" data-testid={`logo-apply-${i}`}>Übernehmen</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BusinessCards = ({ pageId, isPremium, onUpgrade }) => {
  const [f, setF] = useState({ names: "", phone: "", email: "", address: "", quantity: 250, wishes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!isPremium) return <LockedCard text="Individuelle Visitenkarten gestalten wir dir im Premium-Paket." onUpgrade={onUpgrade} testId="cards-locked" />;
  const submit = async () => {
    setBusy(true);
    try { await axios.post(`${API}/pages/${pageId}/business-cards`, { ...f, quantity: Number(f.quantity) }, { headers: authHeaders() }); setSent(true); }
    catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Bitte Namen und eine Menge ab 50 angeben"); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => <div><Label>{label}</Label><Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cards-${k}-input`} {...props} /></div>;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid="business-cards">
      <h3 className="mb-1 flex items-center gap-2 font-semibold text-slate-900"><IdCard className="h-5 w-5 text-indigo-600" /> Visitenkarten anfragen</h3>
      {sent ? <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="cards-sent"><CheckCircle2 className="h-4 w-4" /> Anfrage gesendet! Unser Designer meldet sich mit einem Entwurf bei dir.</p> : (
        <>
          <p className="mb-4 text-sm text-slate-600">Unser Designer gestaltet passende Visitenkarten zu deinem Logo und deiner Webseite und schickt dir die Druckdatei.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Namen & Positionen auf den Karten</Label><Textarea rows={2} value={f.names} onChange={(e) => setF({ ...f, names: e.target.value })} placeholder="Max Mustermann – Inhaber" data-testid="cards-names-input" /></div>
            {field("phone", "Telefon")}{field("email", "E-Mail")}{field("address", "Adresse")}{field("quantity", "Menge (Stück)", { type: "number", min: 50 })}
            <div className="sm:col-span-2"><Label>Wünsche (optional)</Label><Textarea rows={2} value={f.wishes} onChange={(e) => setF({ ...f, wishes: e.target.value })} data-testid="cards-wishes-input" /></div>
          </div>
          <Button onClick={submit} disabled={busy} className="mt-4 rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="cards-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Anfrage senden</Button>
        </>
      )}
    </div>
  );
};

export function BrandingSection({ pageId, isPremium, onUpgrade, onChanged }) {
  return (
    <div className="space-y-6" data-testid="branding-section">
      <LogoGenerator pageId={pageId} onUpgrade={onUpgrade} onChanged={onChanged} />
      <BusinessCards pageId={pageId} isPremium={isPremium} onUpgrade={onUpgrade} />
    </div>
  );
}
